import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Alert, Button, Table } from "@boozallen-uip/uswds-react-core";
import { IndeterminateSpinner } from "@boozallen-uip/uswds-react-loading";
import { Header } from "../components";
import TableNavigation from "../../TableNavigation.component";
import useReport from "../../../hooks/useReport.hook";
import useOpenReportInEditor from "../hooks/useOpenReportInEditor";
import { EvaluationContext } from "../stores/EvaluationContext";
import { formatUTCDisplayDate } from "../../../utlities/common.funcs";
import { ACR_EDITOR_ABOUT_URL, ACR_EDITOR_OVERVIEW_URL } from "../../../utlities/core.constants";

const DEFAULT_PAGE_SIZE = 20;

/** The most recent activity date for a report row. */
function reportActivityTime(report) {
  const raw = report?.lastModifiedDate || report?.createdDate;
  const time = raw ? new Date(raw).getTime() : 0;
  return Number.isNaN(time) ? 0 : time;
}

/**
 * "My Draft ACRs" landing: the authenticated user's own *in-progress* report drafts, scoped
 * server-side by the /report/mine endpoint (status=Draft). It is deliberately a lean resume-work
 * task list — completed ACRs live under "My Product Listings", so this page stays small and needs
 * no filters/search. Resuming reuses the exact open-existing-report flow the About-page typeahead
 * uses (GSAACR-356).
 */
export default function DraftsList() {
  const navigate = useNavigate();
  const { fetchMyReports, deleteReport } = useReport();
  const { openReportInEditor } = useOpenReportInEditor();
  const { evaluation, resetEvaluation } = useContext(EvaluationContext);

  const [reports, setReports] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [openingId, setOpeningId] = useState(null);
  const [openError, setOpenError] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [deleteError, setDeleteError] = useState(null);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    let active = true;
    (async () => {
      setIsLoading(true);
      setLoadError(null);
      try {
        // Only in-progress drafts: this page is a resume-work task list, not a full report browser.
        const list = await fetchMyReports({ status: "Draft" });
        if (active) setReports(Array.isArray(list) ? list : []);
      } catch {
        if (active) {
          setLoadError("We couldn't load your draft ACRs right now. Please try again shortly.");
        }
      } finally {
        if (active) setIsLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [fetchMyReports]);

  const sortedReports = useMemo(
    () => [...reports].sort((a, b) => reportActivityTime(b) - reportActivityTime(a)),
    [reports]
  );

  const totalPages = Math.max(1, Math.ceil(sortedReports.length / pageSize));

  // Keep the current page in range when the list size or page size changes.
  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(1);
  }, [currentPage, totalPages]);

  const pageItems = useMemo(
    () => sortedReports.slice((currentPage - 1) * pageSize, currentPage * pageSize),
    [sortedReports, currentPage, pageSize]
  );

  const handleContinue = useCallback(
    async (report) => {
      if (openingId) return;
      setOpeningId(report.id);
      setOpenError(null);
      const result = await openReportInEditor({
        report,
        previousEvaluation: evaluation,
        navigateToEditor: true,
      });
      if (!result.ok) {
        setOpeningId(null);
        setOpenError(
          "We couldn't open that ACR. Please try again. If this continues, contact support."
        );
      }
    },
    [openingId, openReportInEditor, evaluation]
  );

  const handleDelete = useCallback(
    async (report) => {
      if (deletingId || openingId) return;
      const productName = report.productName || "this draft";
      // eslint-disable-next-line no-alert -- intentional confirm before a destructive delete
      const confirmed = window.confirm(
        `Delete the draft ACR for "${productName}"? It will be removed from your draft ACRs.`
      );
      if (!confirmed) return;
      setDeletingId(report.id);
      setDeleteError(null);
      try {
        await deleteReport(report.id);
        // Server soft-deletes (IsActive=false), which drops it from /report/mine; reflect that here.
        setReports((prev) => prev.filter((r) => r.id !== report.id));
      } catch {
        setDeleteError(
          `We couldn't delete the draft ACR for "${productName}" right now. Please try again shortly.`
        );
      } finally {
        setDeletingId(null);
      }
    },
    [deletingId, openingId, deleteReport]
  );

  const startNewAcr = useCallback(() => {
    // Detach from any draft that was continued this session so About opens on a clean product +
    // catalog, as if the editor were freshly opened.
    resetEvaluation();
    navigate(ACR_EDITOR_ABOUT_URL);
  }, [resetEvaluation, navigate]);

  return (
    <div className="drafts-list-page space-y-4">
      <Header>My Draft ACRs</Header>
      <p>
        Resume an ACR you have in progress. New to the editor?{" "}
        <Link to={ACR_EDITOR_OVERVIEW_URL}>Read the Overview</Link>
      </p>

      <div className="flex flex-wrap gap-2">
        <Button type="button" onClick={startNewAcr}>
          Start a new ACR
        </Button>
      </div>

      {openError && (
        <div role="alert" aria-live="assertive">
          <Alert type="error" heading="Couldn't open ACR" onClose={() => setOpenError(null)}>
            {openError}
          </Alert>
        </div>
      )}

      {deleteError && (
        <div role="alert" aria-live="assertive">
          <Alert type="error" heading="Couldn't delete ACR" onClose={() => setDeleteError(null)}>
            {deleteError}
          </Alert>
        </div>
      )}

      {loadError && (
        <div role="alert" aria-live="assertive">
          <Alert type="error" heading="Couldn't load your ACRs">
            {loadError}
          </Alert>
        </div>
      )}

      {isLoading && <IndeterminateSpinner type="circle" color="blue" />}

      {!isLoading && !loadError && sortedReports.length === 0 && (
        <p className="text-gray-dark">
          You don&apos;t have any draft ACRs in progress. Select <strong>Start a new ACR</strong> to begin.
        </p>
      )}

      {!isLoading && !loadError && sortedReports.length > 0 && (
        <>
          {/* Scrollable wrapper so a wide table stays within the editor's content column (which
              sits beside the sticky "Your report" card) instead of overflowing under it. Matches
              the overflow-x-auto pattern used for the Report page's wide tables. */}
          <div className="overflow-x-auto">
          <Table border={false} style={{ width: "100%" }} className="text-primary-darker">
            <caption className="sr-only">Your draft ACRs, most recently updated first</caption>
            <thead>
              <tr className="[&>th]:text-primary-darker">
                <th scope="col">Product</th>
                <th scope="col">Organization</th>
                <th scope="col">Last modified</th>
                <th scope="col">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {pageItems.map((report) => {
                const productName = report.productName || "Untitled product";
                const orgName = report.vendorName || report.agencyName || "—";
                const lastModified =
                  formatUTCDisplayDate(report.lastModifiedDate || report.createdDate) || "—";
                const isOpeningRow = openingId === report.id;
                const isDeletingRow = deletingId === report.id;
                return (
                  <tr key={report.id}>
                    <td className="text-primary">{productName}</td>
                    <td>{orgName}</td>
                    <td>{lastModified}</td>
                    <td>
                      <div className="flex flex-nowrap gap-2 whitespace-nowrap">
                        <Button
                          type="button"
                          onClick={() => handleContinue(report)}
                          disabled={Boolean(openingId) || Boolean(deletingId)}
                          aria-label={`Continue ${productName} in the editor`}
                        >
                          {isOpeningRow ? "Opening…" : "Continue"}
                        </Button>
                        <Button
                          type="button"
                          outline
                          onClick={() => handleDelete(report)}
                          disabled={Boolean(openingId) || Boolean(deletingId)}
                          aria-label={`Delete draft ACR for ${productName}`}
                        >
                          {isDeletingRow ? "Deleting…" : "Delete"}
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
          </div>

          {sortedReports.length > pageSize && (
            <TableNavigation
              pageSize={pageSize}
              setPageSize={(size) => {
                setPageSize(size);
                setCurrentPage(1);
              }}
              totalPages={totalPages}
              setPage={setCurrentPage}
              currentPage={currentPage}
            />
          )}
        </>
      )}
    </div>
  );
}
